import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PageTemplate from "../../templates/PageTemplate";
import Menu from '../../organisms/Menu';
import { initRecharge, loadBundleData } from '@rechargeapps/storefront-client';
import { getBundleId, getShopDomain, getRechargeToken } from '../../../constants/bundleConfig';

function MenuPage(props) {
    const [mealConstructorCollections, setMealConstructorCollections] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log('[MenuPage] Component mounted, starting to load menu data');
        loadMenuData();
    }, []);

    const processImageUrl = (url) => {
        if (!url) return '';
        return url.replace(/\?v=\d+$/, '');
    };

    const loadMenuData = async () => {
        console.log('[MenuPage] Starting loadMenuData');
        
        try {
            const SHOP_DOMAIN = getShopDomain();
            const RECHARGE_TOKEN = getRechargeToken();
            
            if (!RECHARGE_TOKEN) {
                setIsLoading(false);
                return;
            }

            console.log('[MenuPage] Initializing Recharge SDK');
            initRecharge({
                storeIdentifier: SHOP_DOMAIN,
                storefrontAccessToken: RECHARGE_TOKEN,
                appName: 'ModifyHealth',
                appVersion: '1.0.0'
            });
            const dietTypes = [
                'GLP-1 Plan',
                'Low-FODMAP Plan', 
                'Mediterranean Plan', 
                'Heart-Friendly Plan', 
                'Carb-Conscious Plan',
                'Diabetes Plan',
                'Gluten-Free Plan'
            ];
            
            
            const allCollections = {};

            for (const dietType of dietTypes) {
                const bundleKey = getBundleId(dietType);
                
                if (!bundleKey) {
                    continue;
                }
                
                try {
                    const bundleProduct = await loadBundleData(String(bundleKey), {
                        source: 'online_store'
                    });

                    if (bundleProduct?.collections) {
                        console.log(`[MenuPage] Collections found for ${dietType}:`, Object.keys(bundleProduct.collections));
                        
                        for (const collectionId in bundleProduct.collections) {
                            const collection = bundleProduct.collections[collectionId];
                            const isBreakfast = /breakfast/i.test(collection.title || '');

                            if (!allCollections[collectionId]) {
                                allCollections[collectionId] = {
                                    id: collectionId,
                                    title: collection.title,
                                    handle: collection.handle,
                                    type: isBreakfast ? 'breakfast' : 'entrée',
                                    products: []
                                };
                            }

                            const bundleSellingPlan = bundleProduct.selling_plan_groups?.[0]?.selling_plans?.[0];
                            
                            const products = (collection.products || []).map(product => {
                                const variant = product.variants?.[0];
                                const image = product.featured_image || product.images?.[0] || '';
                                const mealSellingPlanGroups = product.selling_plan_groups ||
                                    (bundleSellingPlan ? [{
                                        selling_plans: [bundleSellingPlan]
                                    }] : []);

                                return {
                                    id: variant?.id || product.id,
                                    collectionId: collectionId,
                                    title: product.title,
                                    handle: product.handle,
                                    skuList: variant?.sku ? `${variant.sku}:1` : '',
                                    price: variant?.price || 0,
                                    image: image.startsWith('//') ? `https:${image}` : image,  
                                    images: (product.images || []).map(img => img.startsWith('//') ? `https:${img}` : img), 
                                    variants: variant ? [{
                                        id: variant.id,
                                        sku: variant.sku,
                                        price: variant.price,
                                        shopify_product_id: product.external_product_id?.ecommerce || product.shopify_product_id || product.id,
                                        shopify_variant_id: variant.shopify_variant_id
                                    }] : [],
                                    type: isBreakfast ? 'breakfast' : 'entrée',
                                    tags: product.tags || [],
                                    description: product.description || '',
                                    selling_plan_groups: mealSellingPlanGroups,
                                    shopify_product_id: product.external_product_id?.ecommerce || product.shopify_product_id || product.id,
                                    dietType: dietType
                                };
                            });

                            allCollections[collectionId].products.push(...products);
                        }
                    } else {
                        console.warn(`[MenuPage] No collections found for ${dietType}`);
                    }
                } catch (dietError) {
                    console.error(`[MenuPage] Error loading bundle for ${dietType}:`, dietError);
                }
            }

            Object.keys(allCollections).forEach(collectionId => {
                const originalCount = allCollections[collectionId].products.length;
                const uniqueProducts = {};
                
                allCollections[collectionId].products.forEach(product => {
                    if (!uniqueProducts[product.id]) {
                        uniqueProducts[product.id] = product;
                    } else {
                        if (product.dietType && !uniqueProducts[product.id].dietTypes) {
                            uniqueProducts[product.id].dietTypes = [uniqueProducts[product.id].dietType];
                        }
                        if (product.dietType) {
                            uniqueProducts[product.id].dietTypes.push(product.dietType);
                        }
                    }
                });
                
                allCollections[collectionId].products = Object.values(uniqueProducts);
                const newCount = allCollections[collectionId].products.length;
            });
            setMealConstructorCollections(allCollections);
            setIsLoading(false);
            setError(null);

        } catch (e) {
            setError(e?.message || 'Error loading menu data');
            setIsLoading(false);
            setMealConstructorCollections(null);
        }
    };

    if (isLoading) {
        return (
            <PageTemplate>
                <div className="loading-menu" style={{ padding: '40px', textAlign: 'center' }}>
                    Loading menu...
                </div>
            </PageTemplate>
        );
    }

    if (error) {
        return (
            <PageTemplate>
                <div className="error-menu" style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
                    Error loading menu: {error}
                </div>
            </PageTemplate>
        );
    }

    return (
        <PageTemplate>
            <Menu
                mealConstructorCollections={mealConstructorCollections}
            />
        </PageTemplate>
    );
}

MenuPage.propTypes = {};

export default MenuPage; 